/**
 * 분할결제 패널 — 좌석별 계산서 (menu #814 / admin #2204)
 *
 * ## 언제 그리지 않는가
 *
 * **분할결제가 꺼진 매장에서는 아무것도 그리지 않는다.** 엄브렐라가 못 박은 원칙이다:
 *
 * > FE 노출 세션 상태는 잔액(saldo) 하나. Bill=1(현재 동작)만 선노출 → 기존 UX 무변화.
 *
 * 판정은 `useSessionBills` 가 한다 — `GET /bills` 가 403 이면 매장 정책 또는 개발
 * 게이트가 닫힌 것이고, 그것은 오류가 아니라 "이 매장은 쓰지 않는다" 는 상태다.
 *
 * 아직 아무도 지분을 나누지 않은 세션(`bills` 가 비어 있음)도 그린다. 이 상태가 바로
 * 첫 분할의 진입점이며, 숨기면 누구도 첫 claim 을 만들 수 없다.
 */
import type { SessionBill, SessionBillsResponse } from '@/lib/api/split-payment';
import type { Currency } from '@/types';
import { formatCurrency } from '@/utils';

interface Props {
  data: SessionBillsResponse | null;
  isSplitAvailable: boolean;
  currency: Currency;
  /**
   * 이 사람이 claim 을 누를 수 있는가.
   *
   * 판정은 `canClaimShare(role, joinStatus)` 가 한다 — 등급뿐 아니라 **합류 상태**도
   * 봐야 승인 대기 중에 버튼이 열리지 않는다(#816 M2).
   */
  canClaim?: boolean;
  /**
   * 잔여에서 내 몫 가져오기. 실패(409 등)는 호출부가 다룬다.
   *
   * ★ **항목 단위가 아니다.** 항목을 고르는 화면(M4 항목별 분모)이 붙기 전까지
   * 이 버튼이 항목 id 를 지어내면 서버는 `order_item_id: min:1` 로 거절한다 —
   * 손님에게는 이유를 알 수 없는 오류로 보인다.
   */
  onClaimResidual?: () => void;
  /** claim 진행 중 — 중복 요청을 막는다. */
  isClaiming?: boolean;
  labels: SplitBillPanelLabels;
}

export interface SplitBillPanelLabels {
  title: string;
  residual: string;
  myBill: string;
  claim: string;
  claiming: string;
  /** 지분을 다 나눠 잔여가 0 일 때 */
  fullyClaimed: string;
}

export function SplitBillPanel({
  data,
  isSplitAvailable,
  currency,
  canClaim = false,
  onClaimResidual,
  isClaiming = false,
  labels,
}: Props) {
  if (!isSplitAvailable || data === null) {
    return null;
  }

  const residualAvailable = data.limits.residual_available_minor;

  return (
    <section className="rounded-lg border border-border bg-card p-4" data-testid="split-bill-panel">
      <h3 className="mb-3 text-sm font-semibold text-foreground">{labels.title}</h3>

      <ul className="flex flex-col gap-2">
        {data.bills.map((bill) => (
          <BillRow
            key={bill.id}
            bill={bill}
            currency={currency}
            isMine={bill.id === data.my_bill_id}
            myBillLabel={labels.myBill}
          />
        ))}
      </ul>

      {data.residual !== null && (
        <div
          className="mt-3 flex items-center justify-between border-t border-border pt-3 text-sm"
          data-testid="split-bill-residual"
        >
          <span className="text-muted-foreground">{labels.residual}</span>
          <span className="font-medium text-foreground">
            {residualAvailable === 0
              ? labels.fullyClaimed
              : formatCurrency(residualAvailable / 100, currency)}
          </span>
        </div>
      )}

      {/* 합류하지 않은 사람(Invitado)에게는 claim 버튼을 그리지 않는다 — D2 는
          "메뉴 열람만" 이고, 누를 수 없는 버튼을 보여주면 합류 안내가 묻힌다. */}
      {canClaim && onClaimResidual !== undefined && residualAvailable > 0 && (
        <button
          type="button"
          onClick={onClaimResidual}
          disabled={isClaiming}
          className="mt-3 w-full rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-50"
          data-testid="split-bill-claim"
        >
          {isClaiming ? labels.claiming : labels.claim}
        </button>
      )}
    </section>
  );
}

function BillRow({
  bill,
  currency,
  isMine,
  myBillLabel,
}: {
  bill: SessionBill;
  currency: Currency;
  isMine: boolean;
  myBillLabel: string;
}) {
  return (
    <li
      className={
        isMine
          ? 'flex items-center justify-between rounded-md bg-primary/10 px-2 py-1.5 text-sm'
          : 'flex items-center justify-between px-2 py-1.5 text-sm'
      }
      data-testid={`split-bill-row-${bill.id}`}
    >
      <span className="flex items-center gap-2">
        <span className="text-foreground">
          {/* 잔여 Bill 은 특정 사람의 것이 아니라 이름이 없다. */}
          {bill.comensal_name ?? `#${bill.sequence_no}`}
        </span>
        {isMine && (
          <span className="rounded bg-primary px-1.5 py-0.5 text-xs text-primary-foreground">
            {myBillLabel}
          </span>
        )}
      </span>

      <span className="flex items-center gap-2">
        <span className="text-foreground">{formatCurrency(bill.total_amount, currency)}</span>
        <span className="text-xs text-muted-foreground">{bill.status_label}</span>
      </span>
    </li>
  );
}
